import { useNamedHeader } from 'hooks/useNamedHeader';
import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { editGameCategoryReducer } from './editGameCategoryReducer';
import {
	EditGameCategoryContext,
	editGameCategoryInitialState,
	TEditGameCategoryVisibleForm,
	TTypeOfEditGameCategoryModalWindow,
} from './editGameCategoryContext';
import { EditGameCategoryActionTypes } from './editGameCategoryActions';
import {
	IItemForGetShortInfo,
	TLanguageIDs,
	IUpdateGameCategory,
	IGameCategoryTranslation,
	IGetGameCategoryInfo,
} from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getGameCategoryInfo } from 'backend/games';
import { AppContext, IAppContext } from 'appState/appContext';

const EditGameCategoryState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editGameCategoryReducer, editGameCategoryInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const categoryID = params.get('categoryID');

	const { response, loading, refetch } = useFetchData<IGetGameCategoryInfo, string>(
		categoryID ? getGameCategoryInfo : null,
		categoryID || '',
	);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditGameCategoryActionTypes.REFETCH_DATA,
				payload: {
					needRefetch: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useNamedHeader(response?.data.gameCategory.name);

	useEffect(() => {
		if (response) {
			dispatch({
				type: EditGameCategoryActionTypes.SET_ALL_DATA,
				payload: {
					categoryID: response.data.gameCategory.id,
					name: response.data.gameCategory.name || '',
					IDsSelectedLanguages: response.data.gameCategory.translations.map((el) => el.languageId),
					translations: response.data.gameCategory.translations,
				},
			});
			dispatch({
				type: EditGameCategoryActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						name: response.data.gameCategory.name || '',
						IDsSelectedLanguages: response.data.gameCategory.translations.map((el) => el.languageId),
						translations: response.data.gameCategory.translations,
					},
				},
			});
		}
	}, [response]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditGameCategoryActionTypes.REFETCH_DATA,
			payload: {
				needRefetch: value,
			},
		});
	}, []);

	const changeVisibleForm = useCallback((value: TEditGameCategoryVisibleForm) => {
		dispatch({
			type: EditGameCategoryActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const changeName = useCallback((value: string) => {
		dispatch({
			type: EditGameCategoryActionTypes.CHANGE_NAME,
			payload: {
				name: value,
			},
		});
	}, []);

	const setFetchedLanguages = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditGameCategoryActionTypes.SET_FETCHED_LANGUAGES,
			payload: {
				languages: value,
			},
		});
	}, []);

	const setIDsLanguages = useCallback(
		(value: TLanguageIDs[]) => {
			dispatch({
				type: EditGameCategoryActionTypes.SET_IDs_LANGUAGES,
				payload: {
					IDsLanguages: value,
				},
			});

			const isNewLanguageAdded = value.length > state.translations.length;
			if (isNewLanguageAdded) {
				const existingTranslationIDs = state.translations.map((el) => el.languageId);
				const newTranslationID = value.find((el) => !existingTranslationIDs.includes(el));
				if (newTranslationID) {
					const newTranslation: IGameCategoryTranslation = {
						languageId: newTranslationID,
						name: '',
					};
					const updatedTranslations = [...state.translations, newTranslation];
					dispatch({
						type: EditGameCategoryActionTypes.SET_TRANSLATION_FIELDS,
						payload: {
							updatedTranslations,
						},
					});
				}
			} else {
				const updatedTranslations = state.translations.filter((el) => value.includes(el.languageId));
				dispatch({
					type: EditGameCategoryActionTypes.SET_TRANSLATION_FIELDS,
					payload: {
						updatedTranslations,
					},
				});
			}
		},
		[state.translations],
	);

	const setDataForSubmit = useCallback((value: IUpdateGameCategory | null) => {
		dispatch({
			type: EditGameCategoryActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				data: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfEditGameCategoryModalWindow) => {
		dispatch({
			type: EditGameCategoryActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: EditGameCategoryActionTypes.SET_TRANSLATION_TAB,
			payload: {
				translationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: EditGameCategoryActionTypes.CHANGE_TRANSLATION,
				payload: {
					updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const resetFields = useCallback(() => dispatch({ type: EditGameCategoryActionTypes.RESET_FIELDS }), []);

	return (
		<EditGameCategoryContext.Provider
			value={{
				refetchData,
				changeVisibleForm,
				changeName,
				setFetchedLanguages,
				setIDsLanguages,
				setDataForSubmit,
				changeTypeOfModalWindow,
				setTranslationTab,
				changeNameTranslation,
				resetFields,
				needRefetchData: state.needRefetchData,
				initialFormData: state.initialFormData,
				categoryID: state.categoryID,
				visibleForm: state.visibleForm,
				name: state.name,
				fetchedLanguages: state.fetchedLanguages,
				IDsSelectedLanguages: state.IDsSelectedLanguages,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
			}}>
			{children}
		</EditGameCategoryContext.Provider>
	);
};

export default EditGameCategoryState;
